<template>
  <div :class="[classes(n()), props.errorMsg ? classes(n('--error')) : '']">
    <div :class="classes(n('--title'))">
      {{ props.title }}
    </div>
    <div
      :class="[classes(n('--container')), focused ? classes(n('--container--focus')) : '', props.disabled ? classes(n('--container--disabled')) : '']"
    >
      <div
        :class="classes(n('--container--prefix'))"
        @click="handlePrefixClick"
      >
        {{ props.currency }}
      </div>
      <div :class="classes(n('--container--content'))">
        <KeyboardInput
          ref="keyboardInput"
          v-model:value="inputValue"
          :class="classes(n('--keyboard-input'))"
          :value.sync="inputValue"
          :before-input="beforeInput"
          :placeholder="inputPlaceholder"
          :placeholder-position="props.placeholderPosition"
          :disabled="props.disabled"
          :decimal="props.decimal"
          :type="props.type"
          :always-show-keyboard="props.alwaysShowKeyboard"
          :maxlength="props.maxlength"
          :confirm-btn-text="props.confirmBtnText"
          :confirm-btn-enable="props.confirmBtnEnable"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          @confirm="handleConfirm"
        />
      </div>
    </div>
    <div
      v-if="$slots.footer || props.errorMsg"
      :class="[classes(n('--footer')), props.errorMsg ? classes(n('--footer--error')) : '']"
    >
      <template v-if="props.errorMsg">
        {{ props.errorMsg }}
      </template>
      <template v-else>
        <slot name="footer" />
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, onMounted } from 'virtual-vue';
import { AmountInputProps } from './props';
import { AmountInputEmits } from './emits';
import { createNamespace } from '../utils/components';
import KeyboardInput from '../keyboard-input/keyboard-input.vue';
import { checkAmount, formatAmount } from './business';
import { yuan2fen } from '@tencent/wphk-utils';
import type { InputResultOptions } from '../keyboard-input/keyboard-input';
import { useAmountInput } from './hooks';

const { name, n, classes } = createNamespace('amount-input');

export default defineComponent({
  name,
  components: { KeyboardInput },
  props: AmountInputProps,
  emits: AmountInputEmits,
  setup(props, { emit }) {
    const focused = ref(false);
    const inputPlaceholder = computed(() => (props.placeholder || (props.decimal ? '0.00' : '0')));
    const keyboardInput = ref<typeof KeyboardInput | null>(null);

    const { inputValue, maxIntDigits, amount, initData, setInputValue } = useAmountInput(props);

    /**
     * 输入前的检测，检测输入的字符是否可以组成有效金额
     * @param newVal 输入后的金额字符串
    */
    const beforeInput = (newVal: string) => checkAmount(newVal, maxIntDigits.value);

    initData();

    onMounted(() => {
      if (props.alwaysShowKeyboard) {
        keyboardInput.value?.focus();
      }
    });

    /** 光标进入时，设置光标位置 */
    function handleFocus() {
      focused.value = true;
      emit('focus');
    };

    /** 光标离开时，格式化金额字符串 */
    function handleBlur() {
      // 光标离开时，格式化金额字符串
      setInputValue(amount.value);
      focused.value = false;
      emit('blur');
    };

    /** 点击输入框键盘confirm按钮触发 */
    function handleConfirm() {
      emit('confirm');
    }

    /** 输入时，格式化金额字符串 */
    function handleInput(value: string, options: InputResultOptions) {
      const parsedValue = formatAmount(value, maxIntDigits.value);
      // 返回单位为分的数字金额值
      const returnValue = parsedValue ? yuan2fen(parsedValue) : 0;
      // 插入文本时，需要将输入的文本转换成符合要求的金额字符串; 删除文本时，金额字符串需保持不变
      inputValue.value = options.inputType === 'insertText' ? parsedValue : value;
      emit('update:value', returnValue);
      emit('input', returnValue);
    };

    /** 点击金额币种input框自动聚焦 */
    async function handlePrefixClick() {
      // 点击此处时，keyboard-input组件内部会同时触发失焦，所以这里需要延时再聚焦
      setTimeout(() => {
        keyboardInput.value?.focus();
        keyboardInput.value?.setCursorPos();
      });
    };

    return {
      n,
      classes,
      props,
      inputValue,
      focused,
      inputPlaceholder,
      keyboardInput,
      handleFocus,
      handleBlur,
      handleConfirm,
      handleInput,
      handlePrefixClick,
      beforeInput,
    };
  },
});

</script>
